import React, { useState } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonButton,
  IonText,
  useIonToast,
  useIonRouter,
  useIonViewWillLeave,
} from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { useAppConfig } from '@/modules/config';
import { FeedbackLogic } from '@/modules/feedback';
import TurnstileWidget from './TurnstileWidget';

const ContactPage: React.FC = () => {
  const { turnstileSiteKey } = useAppConfig();
  const router = useIonRouter();
  const location = useLocation();
  const [presentToast] = useIonToast();

  // Where to go when the form is submitted or dismissed. Callers that route here
  // to keep the user in a different flow (e.g. the beta create-action toast on
  // the Actions list) pass their origin as a `from` query param; absent that we
  // return to the About page this contact form usually hangs off of.
  const queryString = router.routeInfo?.search || location.search;
  const fromParam = new URLSearchParams(queryString).get('from');
  // Only accept a same-app absolute path; reject anything else (external URLs,
  // protocol-relative `//host`) so a crafted `from` can't redirect off-app.
  const returnPath =
    fromParam && fromParam.startsWith('/') && !fromParam.startsWith('//')
      ? fromParam
      : '/about';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // When a site key is configured we require a captcha token; otherwise (dev)
  // the backend skips verification and an empty token is fine.
  const captchaRequired = Boolean(turnstileSiteKey);
  const canSubmit =
    name.trim() !== '' &&
    email.trim() !== '' &&
    message.trim() !== '' &&
    (!captchaRequired || captchaToken !== '') &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await FeedbackLogic.submitFeedback({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        token: captchaToken,
      });
      presentToast({
        message: 'Thanks! Your message is on its way.',
        duration: 3000,
        color: 'success',
        position: 'top',
      });
      router.push(returnPath, 'back', 'pop');
    } catch (err) {
      presentToast({
        message:
          err instanceof Error
            ? err.message
            : 'Could not send your message. Please try again.',
        duration: 4000,
        color: 'danger',
        position: 'top',
      });
      // Force a fresh captcha token for the retry.
      setCaptchaToken('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={returnPath} />
          </IonButtons>
          <IonTitle>Contact</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: '20px' }}>
          <IonText color="medium">
            <p>Questions, problems, or ideas? Send us a note!</p>
          </IonText>

          <IonCard>
            <IonCardContent>
              <IonItem>
                <IonLabel position="stacked">Your name</IonLabel>
                <IonInput
                  value={name}
                  onIonInput={e => setName(e.detail.value ?? '')}
                  autocapitalize="words"
                  data-testid="contact-name"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Your email</IonLabel>
                <IonInput
                  type="email"
                  inputmode="email"
                  value={email}
                  onIonInput={e => setEmail(e.detail.value ?? '')}
                  data-testid="contact-email"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Message</IonLabel>
                <IonTextarea
                  value={message}
                  onIonInput={e => setMessage(e.detail.value ?? '')}
                  autoGrow
                  rows={5}
                  data-testid="contact-message"
                />
              </IonItem>

              {captchaRequired && (
                <div style={{ marginTop: '16px' }}>
                  <TurnstileWidget
                    siteKey={turnstileSiteKey as string}
                    onVerify={setCaptchaToken}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                </div>
              )}

              <IonButton
                expand="block"
                style={{ marginTop: '20px' }}
                disabled={!canSubmit}
                onClick={handleSubmit}
                data-testid="contact-submit"
              >
                {submitting ? 'Sending…' : 'Send message'}
              </IonButton>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ContactPage;
